import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './FloatingChatWidget.css';

const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [unreadCount, setUnreadCount] = useState(0);
  const [position, setPosition] = useState({ bottom: 30, right: 30 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  const API_URL = process.env.REACT_APP_CHATBOT_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message when first opened
      setMessages([{
        id: '1',
        type: 'bot',
        content: "👋 Hi! I'm your AI assistant. Ask me about loans, documents, or approvals!",
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setUnreadCount(0);
      inputRef.current?.focus();
    }
  }, [messages, isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    if (isOpen) return; // Don't allow dragging when chat is open
    
    setIsDragging(true);
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const buttonSize = 64; // Size of the button

    // Calculate new position (relative to bottom-right)
    const newRight = viewportWidth - e.clientX - (buttonSize - dragOffset.x);
    const newBottom = viewportHeight - e.clientY - (buttonSize - dragOffset.y);

    // Keep button within viewport bounds
    const minDistance = 10;
    const maxRight = viewportWidth - buttonSize - minDistance;
    const maxBottom = viewportHeight - buttonSize - minDistance;

    setPosition({
      right: Math.max(minDistance, Math.min(newRight, maxRight)),
      bottom: Math.max(minDistance, Math.min(newBottom, maxBottom))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const toggleChat = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setUnreadCount(0);
      }
    }
  };

  const clearChat = async () => {
    try {
      await fetch(`${API_URL}/api/chat/clear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
    
    // Always clear messages locally, even if API call fails
    setMessages([{
      id: Date.now().toString(),
      type: 'bot',
      content: "✨ Chat cleared! How can I help you?",
      timestamp: new Date()
    }]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const botMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Increment unread count if chat is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (query) => {
    setInputMessage(query);
    inputRef.current?.focus();
  };

  const quickActions = [
    { label: "Recent docs", query: "List 5 recent documents" },
    { label: "Search by company", query: "Search for documents from company 'gri'" },
    { label: "Help", query: "What can you help me with?" }
  ];

  return (
    <>
      {/* Floating Button */}
      <div 
        ref={buttonRef}
        className={`floating-chat-button ${isOpen ? 'open' : ''} ${isDragging ? 'dragging' : ''}`} 
        onClick={toggleChat}
        onMouseDown={handleMouseDown}
        style={{
          bottom: `${position.bottom}px`,
          right: `${position.right}px`,
          cursor: isDragging ? 'grabbing' : (isOpen ? 'pointer' : 'grab')
        }}
      >
        {isOpen ? (
          <span className="close-icon">✕</span>
        ) : (
          <>
            <span className="chat-icon">💬</span>
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount}</span>
            )}
          </>
        )}
      </div>

      {/* Chat Widget */}
      {isOpen && (
        <div 
          className="floating-chat-widget"
          style={{
            bottom: `${position.bottom + 74}px`,
            right: `${position.right}px`
          }}
        >
          <div className="widget-header">
            <div className="header-content">
              <span className="header-icon">🤖</span>
              <div className="header-text">
                <h3>AI Assistant</h3>
                <p>Ask me anything</p>
              </div>
            </div>
            <button 
              className="clear-btn-widget" 
              onClick={clearChat}
              title="Clear conversation"
            >
              🗑️
            </button>
          </div>

          <div className="widget-messages">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`widget-message ${message.type} ${message.isError ? 'error' : ''}`}
              >
                <div className="message-bubble">
                  {message.type === 'bot' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="widget-message bot">
                <div className="message-bubble">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 1 && (
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  className="quick-action-btn"
                  onClick={() => handleQuickAction(action.query)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          <form className="widget-input" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
            />
            <button 
              type="submit" 
              disabled={!inputMessage.trim() || isLoading}
            >
              {isLoading ? '⏳' : '➤'}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default FloatingChatWidget;
